/**
 * Email Service - Lambda Backend Version
 * 
 * This service uses AWS Lambda Function URL instead of direct SES access
 * Benefits:
 * - No AWS credentials in frontend
 * - Better security
 * - Easier to maintain
 * - Can add rate limiting and validation in Lambda
 */

const EMAIL_SERVICE_URL = process.env.REACT_APP_EMAIL_SERVICE_URL;

// Validate that the service URL is configured
if (!EMAIL_SERVICE_URL) {
    console.error('⚠️ EMAIL_SERVICE_URL is not configured in .env file');
    console.error('Add: REACT_APP_EMAIL_SERVICE_URL=https://your-lambda-url.lambda-url.us-east-1.on.aws/');
}

/**
 * Send contact form notification to admin
 * @param {Object} params - Contact form data
 * @param {string} params.name - Sender's name
 * @param {string} params.email - Sender's email
 * @param {string} params.subject - Email subject
 * @param {string} params.message - Email message
 */
export const sendContactNotification = async ({ name, email, subject, message }) => {
    try {
        if (!EMAIL_SERVICE_URL) {
            throw new Error('Email service is not configured. Please contact the administrator.');
        }

        console.log('Sending contact notification via Lambda...');

        const response = await fetch(EMAIL_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'contact_notification',
                name,
                email,
                subject,
                message
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to send email');
        }

        console.log('Contact notification sent successfully:', data.messageId);
        return { success: true, messageId: data.messageId };

    } catch (error) {
        console.error('Error sending contact notification:', error);
        throw new Error(error.message || 'Failed to send message. Please try again.');
    }
};

/**
 * Send confirmation email to user who submitted contact form
 * @param {Object} params - User data
 * @param {string} params.name - User's name
 * @param {string} params.email - User's email
 */
export const sendContactConfirmation = async ({ name, email }) => {
    try {
        if (!EMAIL_SERVICE_URL) {
            throw new Error('Email service is not configured. Please contact the administrator.');
        }

        console.log('Sending contact confirmation via Lambda...');

        const response = await fetch(EMAIL_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'contact_confirmation',
                name,
                email
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to send confirmation email');
        }

        console.log('Contact confirmation sent successfully:', data.messageId);
        return { success: true, messageId: data.messageId };

    } catch (error) {
        console.error('Error sending contact confirmation:', error);
        // Don't throw error for confirmation emails - main notification is more important
        console.warn('Confirmation email failed, but contact form was submitted successfully');
        return { success: false, error: error.message };
    }
};

/**
 * Send newsletter subscription confirmation
 * @param {Object} params - Subscriber data
 * @param {string} params.name - Subscriber's name
 * @param {string} params.email - Subscriber's email
 */
export const sendNewsletterConfirmation = async ({ name, email }) => {
    try {
        if (!EMAIL_SERVICE_URL) {
            throw new Error('Email service is not configured. Please contact the administrator.');
        }

        console.log('Sending newsletter confirmation via Lambda...');

        const response = await fetch(EMAIL_SERVICE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'newsletter_confirmation',
                name,
                email
            })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to send newsletter confirmation');
        }

        console.log('Newsletter confirmation sent successfully:', data.messageId);
        return { success: true, messageId: data.messageId };

    } catch (error) {
        console.error('Error sending newsletter confirmation:', error);
        throw new Error(error.message || 'Failed to send confirmation email. Please try again.');
    }
};

/**
 * Validate email service configuration
 * @returns {Object} Configuration status
 */
export const validateEmailServiceConfig = () => {
    const isConfigured = !!EMAIL_SERVICE_URL;
    
    return {
        isConfigured,
        url: isConfigured ? EMAIL_SERVICE_URL : null,
        message: isConfigured 
            ? 'Email service is properly configured' 
            : 'Email service URL is missing in environment variables'
    };
};

// Export configuration for debugging
export const EMAIL_CONFIG = {
    SERVICE_URL: EMAIL_SERVICE_URL,
    IS_CONFIGURED: !!EMAIL_SERVICE_URL
};
